import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Trophy, Clock, Target, Star, Home, Layers, XCircle, CheckCircle } from 'lucide-react'
import { useGameSession, formatTime } from '../context/GameSessionContext'
import { playCompleteSound } from '../utils/sounds'

interface GameCompleteProps {
  correctCount: number
  totalQuestions: number
  gameName: string
  isMatching?: boolean
  totalRounds?: number
  totalAttempts?: number
}

export default function GameComplete({ correctCount, totalQuestions, gameName, isMatching, totalRounds, totalAttempts }: GameCompleteProps) {
  const { session, endSession } = useGameSession()
  const navigate = useNavigate()

  useEffect(() => {
    playCompleteSound()
  }, [])

  const handleBackToHome = async () => {
    // Master Mode complete: go through Home so it can save + reset cleanly
    if (session.isMasterMode && session.masterGamesCompleted.length >= 4) {
      sessionStorage.setItem('masterModeCompleted', 'true')
      navigate('/')
      return
    }

    // In Master Mode (not complete), don't end session - keep it active for next game
    if (!session.isMasterMode) {
      endSession()
    }
    navigate('/')
  }

  const accuracy = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0
  const matchAccuracy = totalAttempts && totalAttempts > 0 ? Math.round((correctCount / totalAttempts) * 100) : 100
  const wrongAttempts = totalAttempts ? totalAttempts - correctCount : 0
  const isPerfect = isMatching ? (wrongAttempts === 0) : (correctCount === totalQuestions && totalQuestions > 0)

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-6 max-w-sm w-full shadow-2xl text-center animate-flip">
        {/* Trophy */}
        <div className="text-6xl mb-3">
          {isPerfect ? '🏆' : (isMatching ? matchAccuracy : accuracy) >= 70 ? '🎉' : '👏'}
        </div>

        <h2 className="text-2xl font-bold text-white mb-1">
          {isPerfect ? 'Perfect!' : (isMatching ? matchAccuracy : accuracy) >= 70 ? 'Great Job!' : 'Good Try!'}
        </h2>
        <p className="text-white/60 text-sm mb-4">{gameName} Complete</p>

        {/* Stats Grid */}
        <div className={`grid ${isMatching ? 'grid-cols-3' : 'grid-cols-2'} gap-2 mb-5`}>
          {isMatching ? (
            <>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <Layers className="w-4 h-4 text-green-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-green-300">{totalRounds || 3}</p>
                <p className="text-white/60 text-[10px]">Rounds</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <CheckCircle className="w-4 h-4 text-cyan-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-cyan-300">{correctCount}</p>
                <p className="text-white/60 text-[10px]">Pairs Matched</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <XCircle className="w-4 h-4 text-red-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-red-300">{wrongAttempts}</p>
                <p className="text-white/60 text-[10px]">Wrong Tries</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <Target className="w-4 h-4 text-pink-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-pink-300">{totalAttempts || 0}</p>
                <p className="text-white/60 text-[10px]">Total Attempts</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <Trophy className="w-4 h-4 text-amber-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-amber-300">{matchAccuracy}%</p>
                <p className="text-white/60 text-[10px]">Accuracy</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-2.5">
                <Clock className="w-4 h-4 text-blue-300 mx-auto mb-1" />
                <p className="text-xl font-bold text-blue-300">{formatTime(session.elapsedTime)}</p>
                <p className="text-white/60 text-[10px]">Time</p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white/15 rounded-2xl p-3">
                <Target className="w-5 h-5 text-green-300 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-300">{correctCount}/{totalQuestions}</p>
                <p className="text-white/60 text-[10px]">Correct</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-3">
                <Trophy className="w-5 h-5 text-pink-300 mx-auto mb-1" />
                <p className="text-2xl font-bold text-pink-300">{accuracy}%</p>
                <p className="text-white/60 text-[10px]">Accuracy</p>
              </div>
              <div className="bg-white/15 rounded-2xl p-3">
                <Clock className="w-5 h-5 text-blue-300 mx-auto mb-1" />
                <p className="text-2xl font-bold text-blue-300">{formatTime(session.elapsedTime)}</p>
                <p className="text-white/60 text-[10px]">Time</p>
              </div>
            </>
          )}
          <div className="bg-white/15 rounded-2xl p-3">
            <Star className="w-5 h-5 text-yellow-300 mx-auto mb-1" />
            <p className="text-2xl font-bold text-yellow-300">{session.totalScore}</p>
            <p className="text-white/60 text-[10px]">Score</p>
          </div>
        </div>

        {/* Home / Next Game button */}
        {session.isMasterMode ? (
          <button
            onClick={handleBackToHome}
            className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-900 rounded-2xl py-3.5 font-bold
                       active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            {session.masterGamesCompleted.length >= 4 ? (
              <><Home className="w-5 h-5" /> Master Mode Complete!</>
            ) : (
              <><Home className="w-5 h-5" /> Next Game ({session.masterGamesCompleted.length}/4)</>
            )}
          </button>
        ) : (
          <Link
            to="/"
            className="w-full bg-white/20 hover:bg-white/30 text-white rounded-2xl py-3.5 font-bold
                       active:scale-95 transition-all flex items-center justify-center gap-2 shadow-lg"
          >
            <Home className="w-5 h-5" />
            Back to Home
          </Link>
        )}
      </div>
    </div>
  )
}
